import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Baby, LogOut, Shield, Star, Download, Users, Ban, Unlock, Copy, KeyRound, Mail, Search } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PictureReview from "@/components/admin/PictureReview";
import AiStatusBanner from "@/components/admin/AiStatusBanner";
import BillingReview from "@/components/admin/BillingReview";
import ChildAccess from "@/components/admin/ChildAccess";
import ExpiringAccess from "@/components/admin/ExpiringAccess";
import GiftTracker from "@/components/admin/GiftTracker";
import AdminTabs from "@/components/admin/AdminTabs";
import IncomeSummary from "@/components/admin/IncomeSummary";
import ContentTools from "@/components/admin/ContentTools";
import SurveyInvites from "@/components/admin/SurveyInvites";
import ChangePassword from "@/components/admin/ChangePassword";
import KreolHintsAdmin from "@/components/admin/KreolHintsAdmin";
import ReadingAdmin from "@/components/admin/ReadingAdmin";
import WhatsAppLink from "@/components/admin/WhatsAppLink";
import UnpaidParents from "@/components/admin/UnpaidParents";
import OffersEmail from "@/components/admin/OffersEmail";
import QuestionReports from "@/components/admin/QuestionReports";
import PaymentControl from "@/components/admin/PaymentControl";
import PromoCodes from "@/components/admin/PromoCodes";
import EmailStatus from "@/components/admin/EmailStatus";
import CopyAttempts from "@/components/admin/CopyAttempts";
import TestPapersAdmin from "@/components/admin/TestPapersAdmin";

const AVATARS = ["fox", "panda", "robot", "cat", "owl", "dino", "unicorn", "astronaut"];

const ACTION_STYLE = {
  block: "bg-red-900/60 text-red-200",
  unblock: "bg-green-900/60 text-green-200",
  pause_child: "bg-amber-900/60 text-amber-200",
  resume_child: "bg-green-900/60 text-green-200",
  pause_grade: "bg-amber-900/60 text-amber-200",
  resume_grade: "bg-green-900/60 text-green-200",
  password_reset: "bg-blue-900/60 text-blue-200",
  resend_welcome: "bg-slate-700 text-slate-200",
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("parents");
  const [parents, setParents] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [np, setNp] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState(null);
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [gradePauseBusy, setGradePauseBusy] = useState(false);

  const downloadCsv = (cols, data, filename) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.map((c) => c.label).join(",")].concat(
      data.map((d) => cols.map((c) => esc(c.get(d))).join(","))
    );
    const blob = new Blob(["\ufeff" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadLoginsCsv = () => {
    if (!parents.length) { toast.error("No accounts to export yet."); return; }
    downloadCsv([
      { label: "Parent", get: (p) => p.name },
      { label: "Login email", get: (p) => p.email },
      { label: "WhatsApp", get: (p) => p.whatsapp || "" },
      { label: "Consent account msgs", get: (p) => (p.consent_account ? "Yes" : "No") },
      { label: "Consent offers", get: (p) => (p.consent_marketing ? "Yes" : "No") },
      { label: "Password", get: (p) => p.shared_password || "(set a password to include)" },
      { label: "Children", get: (p) => (p.children || []).map((c) => `${c.name} (G${c.grade})`).join("; ") },
      { label: "Access", get: (p) => p.access_state + (p.blocked ? " / blocked" : "") },
      { label: "Login URL", get: () => "https://anouappran.com/login" },
    ], parents, `anou-appran-logins-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const downloadPaymentsCsv = () => {
    const rows = parents.flatMap((p) => (p.payment?.history || []).map((h) => ({ p, h })))
      .sort((a, b) => a.h.month.localeCompare(b.h.month) || a.p.name.localeCompare(b.p.name));
    if (!rows.length) { toast.error("No payments recorded yet."); return; }
    downloadCsv([
      { label: "Month", get: (r) => r.h.label },
      { label: "Parent", get: (r) => r.p.name },
      { label: "Email", get: (r) => r.p.email },
      { label: "Payment reference", get: (r) => r.p.payment?.reference || "" },
      { label: "Children", get: (r) => (r.p.children || []).map((c) => `${c.name} (G${c.grade})`).join("; ") },
      { label: "Payment date", get: (r) => r.h.paid_at },
      { label: "Recorded by", get: (r) => r.h.recorded_by || "" },
    ], rows, `anou-appran-payments-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const downloadSurveysCsv = () => {
    if (!surveys.length) { toast.error("No surveys to export yet."); return; }
    const cols = ["created_at", "name", "email", "rating", "liked", "improve", "support", "comments"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(surveys.map((s) => cols.map((c) => esc(s[c])).join(",")));
    const blob = new Blob(["\ufeff" + rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `anou-appran-surveys-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const parseBulk = () => {
    const parents = [];
    bulkText.split("\n").map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const parts = line.split("|").map((s) => s.trim());
      if (parts.length < 3) return;
      const [name, email, password, childrenStr = ""] = parts;
      const children = childrenStr.split(";").map((c) => c.trim()).filter(Boolean).map((c) => {
        const m = c.match(/^(.*?)(?::\s*(\d))?$/);
        return { name: (m ? m[1] : c).trim(), grade: m && m[2] ? Number(m[2]) : 6 };
      }).filter((c) => c.name);
      parents.push({ name, email, password, children });
    });
    return parents;
  };

  const submitBulk = async () => {
    const parents = parseBulk();
    if (!parents.length) { toast.error("Paste at least one line: Name | email | password | Child:6; Child2:5"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/bulk", { parents });
      toast.success(`Created ${data.created} parent(s), ${data.children} child(ren).${data.skipped.length ? ` Skipped ${data.skipped.length} existing.` : ""}`);
      if (data.errors?.length) toast.error(data.errors.join(" · "));
      setBulkText(""); load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    setBulkBusy(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/parents");
      setParents(data.parents);
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    setLoading(false);
  };
  const loadSurveys = async () => {
    try { const { data } = await api.get("/admin/surveys"); setSurveys(data.surveys); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const loadActivity = async () => {
    try { const { data } = await api.get("/admin/activity"); setActivity(data.activity); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); loadSurveys(); }, []);

  const createParent = async (e) => {
    e.preventDefault();
    if (!np.name || !np.email || np.password.length < 6) { toast.error("Name, email and a 6+ char password are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/parents", np);
      toast.success(`Parent account created for ${np.email}`);
      setNp({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const addChild = async (pid, child, reset) => {
    if (!child.name) { toast.error("Enter the child's name."); return; }
    try {
      await api.post(`/admin/parents/${pid}/children`, child);
      toast.success(`${child.name} added`);
      reset();
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };

  const delParent = async (pid, email) => {
    if (!window.confirm(`Delete ${email} and all their children & progress? This cannot be undone.`)) return;
    try { await api.delete(`/admin/parents/${pid}`); toast.success("Parent deleted"); load(); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const delChild = async (cid) => {
    if (!window.confirm("Delete this child and their progress?")) return;
    try { await api.delete(`/admin/children/${cid}`); toast.success("Child deleted"); load(); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const toggleBlock = async (pid, email, blocked) => {
    let reason = "";
    if (blocked) {
      const r = window.prompt(`Block ${email}? Optionally type a reason the family will see (leave blank to skip):`, "");
      if (r === null) return; // cancelled
      reason = r;
    } else if (!window.confirm(`Unblock ${email}? They will be able to log in again.`)) {
      return;
    }
    try {
      await api.post(`/admin/parents/${pid}/block`, { blocked, reason });
      toast.success(`${email} ${blocked ? "blocked" : "unblocked"}`);
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const toggleChildBlock = async (cid, name, blocked) => {
    let reason = "";
    if (blocked) {
      const r = window.prompt(`Pause ${name}? Optional reason (leave blank to skip):`, "");
      if (r === null) return;
      reason = r;
    } else if (!window.confirm(`Resume ${name}?`)) {
      return;
    }
    try {
      await api.post(`/admin/children/${cid}/block`, { blocked, reason });
      toast.success(`${name} ${blocked ? "paused" : "resumed"}`);
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const copyLogin = async (p) => {
    if (!p.shared_password) {
      toast.error("No saved password for this account yet — use 'Set password' first.");
      return;
    }
    const text = `Anou Appran login\nEmail: ${p.email}\nPassword: ${p.shared_password}\nhttps://anouappran.com/login`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Login details copied — paste them to the parent.");
    } catch {
      window.prompt("Copy the login details:", text);
    }
  };
  const setParentPassword = async (pid, email) => {
    const pw = window.prompt(`Set a new password for ${email} (min 6 chars). It will be saved so you can copy & share it:`, "");
    if (pw === null) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    try {
      await api.post(`/admin/parents/${pid}/password`, { password: pw });
      toast.success("Password updated & saved for sharing.");
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const resendWelcome = async (pid, email) => {
    try {
      await api.post(`/admin/parents/${pid}/resend-welcome`);
      toast.success(`Email re-sent to ${email} (welcome or set-password link)`);
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };
  const pauseGrade = async (grade, blocked) => {
    if (!window.confirm(`${blocked ? "Pause" : "Resume"} ALL Grade ${grade} children across every family?`)) return;
    setGradePauseBusy(true);
    try {
      const { data } = await api.post("/admin/children/pause-grade", { grade, blocked });
      toast.success(`Grade ${grade}: ${data.updated} child(ren) ${blocked ? "paused" : "resumed"}.`);
      load();
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    setGradePauseBusy(false);
  };

  const q = query.trim().toLowerCase();
  const isUnpaid = (p) => Boolean(p.payment?.due_month && !p.payment?.due_paid);
  const unpaidCount = parents.filter(isUnpaid).length;
  const filteredParents = parents.filter((p) => {
    if (unpaidOnly && !isUnpaid(p)) return false;
    if (gradeFilter && !(p.children || []).some((c) => c.grade === gradeFilter)) return false;
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.children || []).some((c) => (c.name || "").toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[#0F172A] text-white font-parent">
      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-yellow-300 text-slate-900 flex items-center justify-center border-2 border-white"><Shield size={22} /></div>
            <div>
              <h1 className="text-2xl font-bold">Admin — Anou Appran Pilot</h1>
              <p className="text-slate-400 text-sm">Create parent & child accounts · monthly plans all year · Holiday Pack 15 Nov – 15 Jan</p>
            </div>
          </div>
          <span className="flex items-center gap-2">
          <ChangePassword />
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="rounded-full border-2 border-slate-600 px-4 py-2 text-sm flex items-center gap-2 hover:bg-slate-800"><LogOut size={15} /> Logout</button>
          </span>
        </div>

        <AiStatusBanner />

        <AdminTabs tab={tab} counts={{ surveys: surveys.length, unpaid: unpaidCount }} onTab={(t) => { setTab(t); if (t === "surveys") loadSurveys(); if (t === "activity") loadActivity(); }} />

        {tab === "parents" ? (
          <>
            {/* create parent */}
            <form onSubmit={createParent} className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-5 mb-6">
              <h2 className="font-bold mb-3 flex items-center gap-2"><UserPlus size={18} /> New parent account</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                <input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Parent name" data-testid="admin-parent-name" className="h-11 px-3 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none focus:border-yellow-300" />
                <input value={np.email} onChange={(e) => setNp({ ...np, email: e.target.value })} placeholder="Email (login)" type="email" data-testid="admin-parent-email" className="h-11 px-3 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none focus:border-yellow-300" />
                <input value={np.password} onChange={(e) => setNp({ ...np, password: e.target.value })} placeholder="Password (min 6)" data-testid="admin-parent-password" className="h-11 px-3 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none focus:border-yellow-300" />
              </div>
              <button disabled={busy} data-testid="admin-create-parent" className="mt-3 rounded-full bg-yellow-300 text-slate-900 font-semibold px-5 py-2.5 border-2 border-yellow-300 disabled:opacity-60">{busy ? "Creating…" : "Create parent"}</button>
              <p className="text-slate-400 text-xs mt-2">The parent and child share this email & password to log in.</p>
            </form>

            {/* bulk add */}
            <details className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-5 mb-6" data-testid="admin-bulk">
              <summary className="font-bold cursor-pointer flex items-center gap-2"><Users size={18} /> Bulk add parents & children</summary>
              <p className="text-slate-400 text-xs mt-3 mb-2">One parent per line: <code className="text-yellow-300">Name | email | password | Child:6; Child2:5</code>. Grade is optional (defaults to 6). Existing emails are skipped.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={5} data-testid="admin-bulk-text"
                placeholder={"Aisha J`n | aisha@mail.com | pass1234 | Liam:6; Ella:5\nRaj K | raj@mail.com | pass1234 | Dev:6"}
                className="w-full rounded-lg bg-slate-900 border-2 border-slate-700 p-3 text-sm font-mono outline-none focus:border-yellow-300" />
              <button onClick={submitBulk} disabled={bulkBusy} data-testid="admin-bulk-submit" className="mt-2 rounded-full bg-yellow-300 text-slate-900 font-semibold px-5 py-2 border-2 border-yellow-300 disabled:opacity-60">{bulkBusy ? "Adding…" : `Add ${parseBulk().length || ""} parent(s)`}</button>
            </details>

            {/* search + bulk pause by grade */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search"
                  placeholder="Search parent name, email or child…"
                  className="w-full h-11 pl-9 pr-3 rounded-lg bg-slate-800 border-2 border-slate-700 outline-none focus:border-yellow-300 text-sm" />
              </div>
              <div className="flex items-center gap-2 bg-slate-800 border-2 border-slate-700 rounded-lg px-3 h-11" data-testid="admin-grade-pause">
                <span className="text-xs text-slate-400 shrink-0">Whole grade:</span>
                {[1, 2, 3, 4, 5, 6].map((g) => (
                  <div key={g} className="flex items-center gap-1">
                    <span className="text-xs font-semibold">G{g}</span>
                    <button onClick={() => pauseGrade(g, true)} disabled={gradePauseBusy} data-testid={`admin-pause-grade-${g}`} title={`Pause all Grade ${g}`} className="text-amber-400 hover:text-amber-300 disabled:opacity-50 p-1"><Ban size={14} /></button>
                    <button onClick={() => pauseGrade(g, false)} disabled={gradePauseBusy} data-testid={`admin-resume-grade-${g}`} title={`Resume all Grade ${g}`} className="text-green-400 hover:text-green-300 disabled:opacity-50 p-1"><Unlock size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* grade filter chips + export logins */}
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="admin-grade-filter">
              <span className="text-xs text-slate-400">Filter:</span>
              <button onClick={() => { setGradeFilter(null); setUnpaidOnly(false); }} data-testid="admin-filter-all" className={`px-3 py-1 rounded-full border-2 text-xs font-semibold ${gradeFilter === null && !unpaidOnly ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600 text-slate-300"}`}>All</button>
              {[1, 2, 3, 4, 5, 6].map((g) => (
                <button key={g} onClick={() => setGradeFilter(gradeFilter === g ? null : g)} data-testid={`admin-filter-g${g}`} className={`px-3 py-1 rounded-full border-2 text-xs font-semibold ${gradeFilter === g ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600 text-slate-300"}`}>Grade {g}</button>
              ))}
              <button onClick={() => setUnpaidOnly((v) => !v)} data-testid="admin-filter-unpaid" className={`px-3 py-1 rounded-full border-2 text-xs font-semibold ${unpaidOnly ? "bg-amber-400 text-slate-900 border-amber-400" : "border-amber-600 text-amber-300"}`}>Unpaid ({unpaidCount})</button>
              <button onClick={downloadPaymentsCsv} data-testid="admin-export-payments" className="ml-auto rounded-full bg-slate-700 hover:bg-slate-600 px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"><Download size={14} /> Export payments (CSV)</button>
              <button onClick={downloadLoginsCsv} data-testid="admin-export-logins" className=" rounded-full bg-slate-700 hover:bg-slate-600 px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"><Download size={14} /> Export logins (CSV)</button>
            </div>

            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-yellow-300" size={32} /></div>
            ) : (
              <div className="space-y-4">
                {parents.length === 0 && <p className="text-slate-400 text-center py-8">No parent accounts yet.</p>}
                {parents.length > 0 && filteredParents.length === 0 && <p className="text-slate-400 text-center py-8" data-testid="admin-no-results">No matches for “{query}”.</p>}
                {filteredParents.map((p) => <ParentCard key={p.id} p={p} onAddChild={addChild} onDelParent={delParent} onDelChild={delChild} onToggleBlock={toggleBlock} onToggleChildBlock={toggleChildBlock} onCopyLogin={copyLogin} onSetPassword={setParentPassword} onResend={resendWelcome} onPayment={(payment) => setParents((ps) => ps.map((x) => (x.id === p.id ? { ...x, payment } : x)))} onReload={load} />)}
              </div>
            )}
          </>
        ) : tab === "kreol" ? (
          <KreolHintsAdmin />
        ) : tab === "reading" ? (
          <ReadingAdmin />
        ) : tab === "survey-invites" ? (
          <SurveyInvites />
        ) : tab === "surveys" ? (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button onClick={downloadSurveysCsv} data-testid="admin-export-csv" className="rounded-full bg-yellow-300 text-slate-900 font-semibold px-4 py-2 text-sm inline-flex items-center gap-2 border-2 border-yellow-300 disabled:opacity-60"><Download size={15} /> Download CSV</button>
            </div>
            {surveys.length === 0 && <p className="text-slate-400 text-center py-8">No survey responses yet.</p>}
            {surveys.map((s, i) => (
              <div key={i} className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4" data-testid={`admin-survey-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold">{s.name} <span className="text-slate-400 font-normal text-sm">· {s.email}</span></span>
                  <span className="flex items-center gap-0.5 text-yellow-300">{Array.from({ length: s.rating || 0 }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm text-slate-300">
                  <p><span className="text-slate-500">Liked:</span> {s.liked || "—"}</p>
                  <p><span className="text-slate-500">Improve:</span> {s.improve || "—"}</p>
                  <p><span className="text-slate-500">Support:</span> {s.support || "—"}</p>
                  <p><span className="text-slate-500">Comments:</span> {s.comments || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "pictures" ? (
          <PictureReview />
        ) : tab === "billing" ? (
          <BillingReview />
        ) : tab === "income" ? (
          <IncomeSummary />
        ) : tab === "reports" ? (
          <QuestionReports />
        ) : tab === "unpaid" ? (
          <UnpaidParents />
        ) : tab === "offers" ? (
          <OffersEmail />
        ) : tab === "promos" ? (
          <PromoCodes />
        ) : tab === "emails" ? (
          <EmailStatus />
        ) : tab === "content" ? (
          <ContentTools />
        ) : tab === "expiring" ? (
          <ExpiringAccess />
        ) : tab === "gifts" ? (
          <GiftTracker />
        ) : tab === "copy" ? (
          <CopyAttempts />
        ) : tab === "papers" ? (
          <TestPapersAdmin />
        ) : (
          <div className="space-y-2" data-testid="admin-activity-list">
            {activity.length === 0 && <p className="text-slate-400 text-center py-8">No admin activity yet.</p>}
            {activity.map((a, i) => (
              <div key={i} className="bg-slate-800 rounded-xl border-2 border-slate-700 px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`admin-activity-${i}`}>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${ACTION_STYLE[a.action] || "bg-slate-700 text-slate-200"}`}>{(a.action || "").replace(/_/g, " ")}</span>
                <span className="text-slate-200">{a.target}</span>
                {a.detail && <span className="text-slate-400 text-xs">· {a.detail}</span>}
                <span className="ml-auto text-slate-500 text-xs shrink-0">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ParentCard({ p, onAddChild, onDelParent, onDelChild, onToggleBlock, onToggleChildBlock, onCopyLogin, onSetPassword, onResend, onPayment, onReload }) {
  const [child, setChild] = useState({ name: "", grade: 6, avatar: "fox" });
  const reset = () => setChild({ name: "", grade: 6, avatar: "fox" });
  return (
    <div className={`rounded-2xl border-2 p-5 ${p.blocked ? "bg-red-950/40 border-red-700" : "bg-slate-800 border-slate-700"}`} data-testid={`admin-parent-${p.email}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-lg">{p.name}</p>
          <p className="text-slate-400 text-sm flex items-center gap-2 flex-wrap">{p.email} <WhatsAppLink number={p.whatsapp} name={p.name} consentAccount={p.consent_account} consentMarketing={p.consent_marketing} testId={`admin-whatsapp-${p.email}`} /></p>
          <div className="flex gap-2 mt-1 text-xs flex-wrap">
            <span className="px-2 py-0.5 rounded-full bg-slate-900 border border-slate-600 capitalize">{p.plan}</span>
            <span className={`px-2 py-0.5 rounded-full border ${p.access_state === "active" ? "bg-green-900/40 border-green-600 text-green-300" : p.access_state === "survey_required" ? "bg-amber-900/40 border-amber-600 text-amber-300" : "bg-red-900/40 border-red-600 text-red-300"}`}>{p.access_state}</span>
            {p.survey_completed && <span className="px-2 py-0.5 rounded-full bg-blue-900/40 border border-blue-600 text-blue-300">survey done</span>}
            {p.blocked && <span className="px-2 py-0.5 rounded-full bg-red-900/60 border border-red-500 text-red-200 font-semibold inline-flex items-center gap-1" data-testid={`admin-blocked-badge-${p.email}`}><Ban size={11} /> blocked</span>}
          </div>
          {p.blocked && p.blocked_reason && <p className="text-red-300 text-xs mt-1 italic">Reason shown to family: “{p.blocked_reason}”</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onCopyLogin(p)} data-testid={`admin-copy-login-${p.email}`} title="Copy login details" className="text-slate-300 hover:text-white p-2"><Copy size={16} /></button>
          <button onClick={() => onResend(p.id, p.email)} data-testid={`admin-resend-${p.email}`} title="Re-send welcome email" className="text-slate-300 hover:text-white p-2"><Mail size={16} /></button>
          <button onClick={async () => { try { await api.post(`/admin/parents/${p.id}/preview-young`, { live: !p.preview_young }); toast.success(p.preview_young ? "Grade 1–3 preview off" : "This family can now preview Grades 1–3"); onReload(); } catch (e) { toast.error(formatError(e?.response?.data?.detail)); } }}
            data-testid={`admin-preview-young-${p.email}`} title="Let this family preview Grades 1–3 before they go live"
            className={`text-xs font-bold rounded-full px-2 py-1 ${p.preview_young ? "bg-pink-500 text-white" : "text-slate-400 border border-slate-600 hover:text-white"}`}>G1–3</button>
          <button onClick={() => onSetPassword(p.id, p.email)} data-testid={`admin-set-password-${p.email}`} title="Set / reset password" className="text-slate-300 hover:text-white p-2"><KeyRound size={16} /></button>
          <button
            onClick={() => onToggleBlock(p.id, p.email, !p.blocked)}
            data-testid={`admin-block-parent-${p.email}`}
            title={p.blocked ? "Unblock account" : "Block access"}
            className={`p-2 rounded-lg ${p.blocked ? "text-green-400 hover:text-green-300" : "text-amber-400 hover:text-amber-300"}`}>
            {p.blocked ? <Unlock size={16} /> : <Ban size={16} />}
          </button>
          <button onClick={() => onDelParent(p.id, p.email)} data-testid={`admin-del-parent-${p.email}`} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
        </div>
      </div>

      {p.shared_password && (
        <p className="text-slate-500 text-xs mt-2">Login to share — <span className="text-slate-300">{p.email}</span> · password <span className="text-slate-300 font-mono">{p.shared_password}</span></p>
      )}

      <div className="mt-3 pl-1">
        {p.children.length === 0 ? <p className="text-slate-500 text-sm">No children yet.</p> : (
          <div className="flex flex-wrap gap-2">
            {p.children.map((c) => (
              <span key={c.id} className={`inline-flex items-center gap-2 border rounded-full pl-3 pr-1.5 py-1 text-sm ${c.blocked ? "bg-red-950/60 border-red-600 text-red-200" : "bg-slate-900 border-slate-600"}`} data-testid={`admin-child-${c.name}`}>
                <Baby size={14} className={c.blocked ? "text-red-300" : "text-yellow-300"} /> {c.name} · G{c.grade}{c.blocked && " · paused"}
                <button onClick={() => onToggleChildBlock(c.id, c.name, !c.blocked)} data-testid={`admin-block-child-${c.id}`} title={c.blocked ? "Resume this child" : "Pause this child"} className={`p-0.5 ${c.blocked ? "text-green-400 hover:text-green-300" : "text-amber-400 hover:text-amber-300"}`}>{c.blocked ? <Unlock size={13} /> : <Ban size={13} />}</button>
                <button onClick={() => onDelChild(c.id)} className="text-red-400 hover:text-red-300 p-0.5"><Trash2 size={13} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {p.children.length > 0 && (
        <div className="mt-2 pl-1 flex flex-col gap-1">
          {p.children.map((c) => <ChildAccess key={c.id} child={c} onSaved={onReload} />)}
        </div>
      )}

      {p.plan === "pilot" && <PaymentControl parent={p} onChange={onPayment} />}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-700 pt-3">
        <input value={child.name} onChange={(e) => setChild({ ...child, name: e.target.value })} placeholder="Child name" data-testid={`admin-child-name-${p.email}`} className="h-9 px-3 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none focus:border-yellow-300 text-sm" />
        <select value={child.grade} onChange={(e) => setChild({ ...child, grade: Number(e.target.value) })} data-testid={`admin-child-grade-${p.email}`} className="h-9 px-2 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none text-sm">
          {[6, 5, 4, 3, 2, 1].map((g) => <option key={g} value={g}>Grade {g}</option>)}
        </select>
        <select value={child.avatar} onChange={(e) => setChild({ ...child, avatar: e.target.value })} className="h-9 px-2 rounded-lg bg-slate-900 border-2 border-slate-700 outline-none text-sm capitalize">
          {AVATARS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button onClick={() => onAddChild(p.id, child, reset)} data-testid={`admin-add-child-${p.email}`} className="h-9 rounded-full bg-slate-700 hover:bg-slate-600 px-4 text-sm font-semibold inline-flex items-center gap-1.5"><UserPlus size={14} /> Add child</button>
      </div>
    </div>
  );
}
